import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Bell, Send, ArrowLeft, Users, CheckCircle, XCircle,
  Loader, AlertTriangle, Info, Mail, Smartphone, Link,
  FileText, Layers, Search
} from 'lucide-react';
import {
  getNotificationCategories,
  getUsersForNotificationTargeting,
  getCollectionsForTargeting,
  getArtistsInCollection,
  getEmailTemplates,
  sendEmailToUsers,
  getEventsForTargeting,
  getNonTicketPurchasersForEvent
} from '../utils/adminApi';
import { supabase } from '../utils/supabaseClient';
import '../styles/SendNotificationPage.css';

const SendNotificationPage = () => {
  const navigate = useNavigate();

  // Form state
  const [sendForm, setSendForm] = useState({
    title: '',
    message: '',
    category_key: '',
    target_type: 'all_users', // 'all_users', 'user_types', 'collection', 'event_non_purchasers'
    user_types: [],
    collection_id: '',
    event_id: '',
    priority: 1,
    action_url: '',
    // Channel options
    send_push: true,
    send_email: false,
    email_template: '',
    email_subject: '',
    include_test_accounts: false
  });

  // UI state
  const [categories, setCategories] = useState([]);
  const [collections, setCollections] = useState([]);
  const [events, setEvents] = useState([]);
  const [eventPurchaserCount, setEventPurchaserCount] = useState(0);
  const [eventSearchTerm, setEventSearchTerm] = useState('');
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [previewUsers, setPreviewUsers] = useState([]);
  const [totalUsers, setTotalUsers] = useState(0);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [loadingCollections, setLoadingCollections] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchDebounce, setSearchDebounce] = useState(null);

  // Sending state
  const [sending, setSending] = useState(false);
  const [sendProgress, setSendProgress] = useState({
    total: 0,
    sent: 0,
    failed: 0,
    emailSent: 0,
    emailFailed: 0,
    pushSent: 0,
    pushFailed: 0,
    current: null,
    results: []
  });
  const [sendComplete, setSendComplete] = useState(false);

  // Step state
  const [currentStep, setCurrentStep] = useState(1);

  // User search state (for specific_user targeting and test mode user selection)
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [userSearchResults, setUserSearchResults] = useState([]);
  const [searchingUsers, setSearchingUsers] = useState(false);

  // Load categories, collections, and templates on mount
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        const [categoriesData, collectionsData, templatesData, eventsData] = await Promise.all([
          getNotificationCategories(),
          getCollectionsForTargeting(),
          getEmailTemplates(),
          getEventsForTargeting()
        ]);
        setCategories(categoriesData);
        setCollections(collectionsData);
        setEmailTemplates(templatesData);
        setEvents(eventsData);
      } catch (error) {
        console.error('Error loading initial data:', error);
      }
    };
    loadInitialData();
  }, []);

  // Load preview users when filters change
  useEffect(() => {
    loadPreviewUsers();
  }, [sendForm.target_type, JSON.stringify(sendForm.user_types), sendForm.collection_id, sendForm.event_id, sendForm.send_push, sendForm.send_email, sendForm.include_test_accounts, selectedUserId]);

  // Debounced search
  useEffect(() => {
    if (searchDebounce) clearTimeout(searchDebounce);
    const timeout = setTimeout(() => {
      loadPreviewUsers();
    }, 300);
    setSearchDebounce(timeout);
    return () => clearTimeout(timeout);
  }, [searchTerm]);

  const loadPreviewUsers = async () => {
    setLoadingPreview(true);
    try {
      let users = [];
      let total = 0;

      if (sendForm.target_type === 'specific_user') {
        if (selectedUserId) {
          // Fetch the selected user directly by UUID
          const { data: userData, error: userError } = await supabase
            .from('users')
            .select('uuid, email, first_name, last_name, nickname, preferences, test_account')
            .eq('uuid', selectedUserId)
            .single();

          if (!userError && userData) {
            // Check if user has the app installed
            const { data: playerData } = await supabase
              .from('user_player_ids')
              .select('user_id')
              .eq('user_id', selectedUserId)
              .limit(1);

            users = [{ ...userData, hasApp: (playerData?.length || 0) > 0 }];
          }
          total = users.length;
        } else {
          users = [];
          total = 0;
        }
      } else if (sendForm.target_type === 'collection' && sendForm.collection_id) {
        // Get artists from the selected collection
        const result = await getArtistsInCollection(sendForm.collection_id, {
          hasAppOnly: sendForm.send_push && !sendForm.send_email,
          hasEmailOnly: sendForm.send_email && !sendForm.send_push,
          searchTerm
        });
        users = Array.isArray(result) ? result : (result.users || []);
        total = result.total || users.length;
      } else if (sendForm.target_type === 'event_non_purchasers' && sendForm.event_id) {
        // Get users who have NOT purchased tickets for the selected event
        // Filter to app-only users when push is the only channel
        const selectedEvent = events.find(e => String(e.id) === String(sendForm.event_id));
        const result = await getNonTicketPurchasersForEvent(
          sendForm.event_id,
          selectedEvent?.eventbrite_id || null,
          {
            include_test_accounts: sendForm.include_test_accounts,
            has_app_only: sendForm.send_push && !sendForm.send_email
          }
        );
        users = result.users || [];
        total = result.total || users.length;
        setEventPurchaserCount(result.purchaserCount || 0);
      } else {
        // Original logic for all users / user types
        const userTypes = sendForm.target_type === 'user_types' && sendForm.user_types.length > 0
          ? sendForm.user_types
          : ['artist', 'collector'];

        console.log('Loading preview for user types:', userTypes, 'search:', searchTerm);

        const result = await getUsersForNotificationTargeting({
          user_types: userTypes,
          has_app_only: sendForm.send_push && !sendForm.send_email,
          search_term: searchTerm,
          include_test_accounts: sendForm.include_test_accounts,
          limit: 100
        });
        users = result.users || [];
        total = result.total || users.length;
      }

      console.log(`Preview loaded: ${users.length} users (total: ${total})`);

      setPreviewUsers(users);
      setTotalUsers(total);
    } catch (error) {
      console.error('Error loading preview users:', error);
      setPreviewUsers([]);
      setTotalUsers(0);
    } finally {
      setLoadingPreview(false);
    }
  };

  const searchUsers = async (term) => {
    if (!term || term.length < 2) {
      setUserSearchResults([]);
      return;
    }
    setSearchingUsers(true);
    try {
      const result = await getUsersForNotificationTargeting({
        search_term: term,
        include_test_accounts: sendForm.include_test_accounts,
        limit: 20
      });
      setUserSearchResults(result.users || []);
    } catch (error) {
      console.error('Error searching users:', error);
      setUserSearchResults([]);
    } finally {
      setSearchingUsers(false);
    }
  };

  // Debounced user search for specific_user targeting and test mode
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (sendForm.target_type === 'specific_user') {
        searchUsers(userSearchTerm);
      }
    }, 300);
    return () => clearTimeout(timeout);
  }, [userSearchTerm, sendForm.target_type]);

  const handleSendNotification = async () => {
    if (!sendForm.title || !sendForm.message) {
      alert('Please fill in title and message');
      return;
    }

    if (!sendForm.send_push && !sendForm.send_email) {
      alert('Please select at least one channel (Push or Email)');
      return;
    }

    if (sendForm.send_push && !sendForm.category_key) {
      alert('Please select a category for push notifications');
      return;
    }

    setSending(true);
    setSendComplete(false);

    try {
      // Determine target users - fetch ALL users for sending (not just preview limit)
      let targetUsers = [];

      {
        if (sendForm.target_type === 'specific_user' && selectedUserId) {
          targetUsers = previewUsers; // Already filtered to just the selected user
        } else if (sendForm.target_type === 'collection' && sendForm.collection_id) {
          const result = await getArtistsInCollection(sendForm.collection_id, {
            hasAppOnly: sendForm.send_push && !sendForm.send_email,
            hasEmailOnly: sendForm.send_email && !sendForm.send_push
          });
          targetUsers = Array.isArray(result) ? result : (result.users || []);
        } else if (sendForm.target_type === 'event_non_purchasers' && sendForm.event_id) {
          const selectedEvent = events.find(e => String(e.id) === String(sendForm.event_id));
          const result = await getNonTicketPurchasersForEvent(
            sendForm.event_id,
            selectedEvent?.eventbrite_id || null,
            {
              include_test_accounts: sendForm.include_test_accounts,
              has_app_only: sendForm.send_push && !sendForm.send_email
            }
          );
          targetUsers = result.users || [];
        } else {
          const userTypes = sendForm.target_type === 'user_types' && sendForm.user_types.length > 0
            ? sendForm.user_types
            : ['artist', 'collector'];

          const result = await getUsersForNotificationTargeting({
            user_types: userTypes,
            has_app_only: sendForm.send_push && !sendForm.send_email,
            include_test_accounts: sendForm.include_test_accounts,
            limit: 5000  // Higher limit for actual sending
          });
          targetUsers = result.users || [];
        }
      }

      if (targetUsers.length === 0) {
        alert('No users found to send notification to');
        setSending(false);
        return;
      }

      // Initialize progress
      setSendProgress({
        total: targetUsers.length,
        sent: 0,
        failed: 0,
        emailSent: 0,
        emailFailed: 0,
        pushSent: 0,
        pushFailed: 0,
        current: null,
        results: []
      });

      console.log(`Starting send to ${targetUsers.length} user(s) - Push: ${sendForm.send_push}, Email: ${sendForm.send_email}`);

      // Send to each user
      for (let i = 0; i < targetUsers.length; i++) {
        const user = targetUsers[i];

        setSendProgress(prev => ({
          ...prev,
          current: user?.nickname || user?.email || user.uuid.substring(0, 8)
        }));

        let pushSuccess = false;
        let emailSuccess = false;

        // Always call edge function to create notification record (even for users without the app)
        // The edge function will create the in-app notification and attempt push if player IDs exist
        if (sendForm.send_push) {
          try {
            const { data, error } = await supabase.functions.invoke('onesignal_push_artist_v2', {
              body: {
                externalId: user.uuid,
                senderId: null,
                senderType: 'system',
                title: sendForm.title,
                message: sendForm.message,
                category: sendForm.category_key,
                priority: sendForm.priority,
                metadata: {
                  admin_broadcast: true,
                  broadcast_timestamp: new Date().toISOString(),
                  recipient_count: targetUsers.length,
                  sent_by_admin: true,
                  collection_id: sendForm.collection_id || null,
                  action_url: sendForm.action_url || null
                }
              }
            });

            if (!error && data?.push_sent) {
              pushSuccess = true;
              setSendProgress(prev => ({ ...prev, pushSent: prev.pushSent + 1 }));
            } else if (!error && data?.in_app_created) {
              // Notification record created but push not sent (user doesn't have app)
              pushSuccess = true; // Still counts as success for in-app notification
              setSendProgress(prev => ({ ...prev, pushSent: prev.pushSent + 1 }));
            } else {
              console.error(`Push failed for ${user.uuid}:`, error || data);
              setSendProgress(prev => ({ ...prev, pushFailed: prev.pushFailed + 1 }));
            }
          } catch (err) {
            console.error(`Push error for ${user.uuid}:`, err);
            setSendProgress(prev => ({ ...prev, pushFailed: prev.pushFailed + 1 }));
          }
        }

        // Send email if enabled
        if (sendForm.send_email && user.email) {
          try {
            const emailResults = await sendEmailToUsers([user], {
              subject: sendForm.email_subject || sendForm.title,
              templateName: sendForm.email_template || 'admin-notification',
              htmlBody: sendForm.message,
              mergeVars: [
                { name: 'NOTIFICATION_TITLE', content: sendForm.title },
                { name: 'NOTIFICATION_MESSAGE', content: sendForm.message },
                { name: 'ACTION_URL', content: sendForm.action_url || '' }
              ]
            });

            if (emailResults[0]?.success) {
              emailSuccess = true;
              setSendProgress(prev => ({ ...prev, emailSent: prev.emailSent + 1 }));
            } else {
              setSendProgress(prev => ({ ...prev, emailFailed: prev.emailFailed + 1 }));
            }
          } catch (err) {
            console.error(`Email error for ${user.uuid}:`, err);
            setSendProgress(prev => ({ ...prev, emailFailed: prev.emailFailed + 1 }));
          }
        }

        // Update overall progress
        const overallSuccess = (sendForm.send_push ? pushSuccess : true) && (sendForm.send_email ? emailSuccess : true);
        setSendProgress(prev => ({
          ...prev,
          sent: overallSuccess ? prev.sent + 1 : prev.sent,
          failed: !overallSuccess ? prev.failed + 1 : prev.failed,
          results: [...prev.results, {
            userId: user.uuid,
            success: overallSuccess,
            user: user?.nickname || user?.email,
            pushSuccess,
            emailSuccess
          }]
        }));
      }

      setSendComplete(true);
      console.log('Send complete');

    } catch (error) {
      console.error('Error in send process:', error);
      alert(`Failed to send notifications: ${error.message}`);
    } finally {
      setSending(false);
      setSendProgress(prev => ({ ...prev, current: null }));
    }
  };

  const progressPercentage = sendProgress.total > 0
    ? Math.round(((sendProgress.sent + sendProgress.failed) / sendProgress.total) * 100)
    : 0;

  // Get selected collection name
  const selectedCollection = collections.find(c => c.collection_id === parseInt(sendForm.collection_id));

  // Step indicator component
  const StepIndicator = () => (
    <div className="send-step-indicator">
      {[
        { num: 1, label: 'Channel & Audience' },
        { num: 2, label: 'Compose' },
        { num: 3, label: 'Review & Send' },
      ].map((step, i) => (
        <React.Fragment key={step.num}>
          {i > 0 && <div className={`send-step-connector ${currentStep > step.num ? 'completed' : ''}`} />}
          <div className={`send-step ${currentStep === step.num ? 'active' : ''} ${currentStep > step.num ? 'completed' : ''}`}>
            <div className="send-step-number">
              {currentStep > step.num ? '✓' : step.num}
            </div>
            <span>{step.label}</span>
          </div>
        </React.Fragment>
      ))}
    </div>
  );

  // Recipient preview panel (used in step 2)
  const RecipientPreview = () => (
    <div className="preview-section">
      <div className="preview-header">
        <h3>Recipients Preview</h3>
        <div className="preview-header-right">
          <label className="header-checkbox">
            <input
              type="checkbox"
              checked={sendForm.include_test_accounts}
              onChange={(e) => setSendForm(prev => ({ ...prev, include_test_accounts: e.target.checked }))}
            />
            <span>Include test accounts</span>
          </label>
          {loadingPreview ? (
            <span className="loading-text">Loading...</span>
          ) : (
            <span className="count-badge">
              {totalUsers > previewUsers.length
                ? `${previewUsers.length} of ${totalUsers}`
                : `${previewUsers.length}`} user{totalUsers !== 1 ? 's' : ''}
            </span>
          )}
        </div>
      </div>

      {/* Search input */}
      <div className="search-input-wrapper">
        <Search size={16} className="search-icon" />
        <input
          type="text"
          placeholder="Search by name or email..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
        {searchTerm && (
          <button
            className="clear-search"
            onClick={() => setSearchTerm('')}
          >
            ×
          </button>
        )}
      </div>

      {/* Channel breakdown stats */}
      {previewUsers.length > 0 && (sendForm.send_push || sendForm.send_email) && (
        <div className="channel-breakdown">
          {sendForm.send_push && (
            <div className="channel-stat">
              <Smartphone size={16} />
              <span>{previewUsers.filter(u => u.hasApp).length} can receive push</span>
            </div>
          )}
          {sendForm.send_email && (
            <div className="channel-stat">
              <Mail size={16} />
              <span>{previewUsers.filter(u => u.email).length} can receive email</span>
            </div>
          )}
          {sendForm.send_push && !sendForm.send_email && (
            <div className="channel-note">
              {previewUsers.filter(u => !u.hasApp).length} users don't have the app
            </div>
          )}
        </div>
      )}

      {previewUsers.length > 0 ? (
        <div className="preview-list">
          {previewUsers.slice(0, 50).map((user, idx) => (
            <div
              key={user.uuid}
              className="preview-user"
            >
              <span className="user-number">{idx + 1}.</span>
              <span className="user-name">
                {user.nickname || user.first_name || user.email}
                {user.test_account && <span className="test-badge">TEST</span>}
              </span>
              <span className="user-email">{user.email}</span>
              <span className="user-channels">
                {user.hasApp && sendForm.send_push && <Smartphone size={14} title="Will receive push" />}
                {user.email && sendForm.send_email && <Mail size={14} title="Will receive email" />}
              </span>
            </div>
          ))}
          {previewUsers.length > 50 && (
            <div className="preview-more">
              ...and {totalUsers - 50} more {searchTerm ? 'matching' : ''} users
            </div>
          )}
        </div>
      ) : (
        <div className="preview-empty">
          <Users size={48} />
          <p>No users found for selected criteria</p>
        </div>
      )}
    </div>
  );

  return (
    <div className="send-notification-page">
      <div className="send-notification-container">
        {/* Header */}
        <div className="send-notification-header">
          <button
            className="back-button"
            onClick={() => navigate('/admin/notifications')}
            disabled={sending}
          >
            <ArrowLeft size={20} />
            Back
          </button>
          <div>
            <h1 className="page-title">
              <Bell size={24} />
              Send Push Notifications
            </h1>
            <p className="page-subtitle">Send push notifications to mobile app users</p>
          </div>
        </div>

        {/* Step Indicator */}
        <StepIndicator />

        {/* Step 1: Channel & Audience */}
        {currentStep === 1 && (
          <div className="send-notification-content" style={{ gridTemplateColumns: '1fr' }}>
            <div className="form-column">
              {/* Channel Selection */}
              <div className="form-section channel-section">
                <h3><Smartphone size={18} /> Push Notification</h3>
                <p style={{ fontSize: '0.8125rem', color: '#6b7280', margin: '0 0 0.5rem 0' }}>
                  Push notifications are sent to users with the mobile app installed. For emails, use the <a href="/curator/communications" style={{ color: '#7c3aed' }}>Communications Hub</a>.
                </p>
              </div>

              {/* Target Audience */}
              <div className="form-section">
                <h3><Users size={18} /> Target Audience</h3>

                <div className="radio-group">
                  <label className="radio-label">
                    <input
                      type="radio"
                      checked={sendForm.target_type === 'all_users'}
                      onChange={() => setSendForm(prev => ({ ...prev, target_type: 'all_users', collection_id: '', event_id: '' }))}
                      disabled={sending}
                    />
                    <span>All Mobile Users</span>
                  </label>
                  <label className="radio-label">
                    <input
                      type="radio"
                      checked={sendForm.target_type === 'user_types'}
                      onChange={() => setSendForm(prev => ({ ...prev, target_type: 'user_types', collection_id: '', event_id: '' }))}
                      disabled={sending}
                    />
                    <span>Specific User Types</span>
                  </label>
                  <label className="radio-label">
                    <input
                      type="radio"
                      checked={sendForm.target_type === 'collection'}
                      onChange={() => setSendForm(prev => ({ ...prev, target_type: 'collection' }))}
                      disabled={sending}
                    />
                    <span>Artists in Collection</span>
                  </label>
                  <label className="radio-label">
                    <input
                      type="radio"
                      checked={sendForm.target_type === 'specific_user'}
                      onChange={() => {
                        setSendForm(prev => ({ ...prev, target_type: 'specific_user', collection_id: '', event_id: '' }));
                        setSelectedUserId(null);
                        setUserSearchTerm('');
                        setUserSearchResults([]);
                      }}
                      disabled={sending}
                    />
                    <span>Specific User</span>
                  </label>
                  <label className="radio-label">
                    <input
                      type="radio"
                      checked={sendForm.target_type === 'event_non_purchasers'}
                      onChange={() => {
                        setSendForm(prev => ({ ...prev, target_type: 'event_non_purchasers', collection_id: '', event_id: '' }));
                        setSelectedEvent(null);
                        setEventSearchTerm('');
                      }}
                      disabled={sending}
                    />
                    <span>Event Non-Purchasers</span>
                  </label>
                </div>

                {sendForm.target_type === 'user_types' && (
                  <div className="checkbox-group">
                    {['artist', 'collector'].map(type => (
                      <label key={type} className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={sendForm.user_types.includes(type)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSendForm(prev => ({ ...prev, user_types: [...prev.user_types, type] }));
                            } else {
                              setSendForm(prev => ({ ...prev, user_types: prev.user_types.filter(t => t !== type) }));
                            }
                          }}
                          disabled={sending}
                        />
                        <span>{type.charAt(0).toUpperCase() + type.slice(1)}s</span>
                      </label>
                    ))}
                  </div>
                )}

                {sendForm.target_type === 'collection' && (
                  <div className="collection-select">
                    <label>Select Collection</label>
                    <select
                      value={sendForm.collection_id}
                      onChange={(e) => setSendForm(prev => ({ ...prev, collection_id: e.target.value }))}
                      disabled={sending}
                    >
                      <option value="">Choose a collection...</option>
                      {collections.map((collection) => (
                        <option key={collection.collection_id} value={collection.collection_id}>
                          {collection.title} ({collection.type})
                        </option>
                      ))}
                    </select>
                    {selectedCollection && (
                      <p className="field-hint">
                        Targeting artists who have artwork in "{selectedCollection.title}"
                      </p>
                    )}
                  </div>
                )}

                {sendForm.target_type === 'event_non_purchasers' && (
                  <div style={{ marginTop: '0.75rem' }}>
                    <div className="search-input-wrapper">
                      <Search size={16} className="search-icon" />
                      <input
                        type="text"
                        placeholder="Search events by name..."
                        value={eventSearchTerm}
                        onChange={(e) => setEventSearchTerm(e.target.value)}
                        className="search-input"
                        disabled={sending}
                      />
                      {eventSearchTerm && (
                        <button className="clear-search" onClick={() => setEventSearchTerm('')}>×</button>
                      )}
                    </div>
                    {eventSearchTerm && !selectedEvent && (
                      <div style={{ maxHeight: '250px', overflowY: 'auto', marginTop: '0.5rem', border: '1px solid #e5e7eb', borderRadius: '0.5rem' }}>
                        {events
                          .filter(e => e.name?.toLowerCase().includes(eventSearchTerm.toLowerCase()))
                          .map(event => (
                            <div
                              key={event.id}
                              onClick={() => {
                                setSelectedEvent(event);
                                setSendForm(prev => ({ ...prev, event_id: String(event.id) }));
                                setEventSearchTerm('');
                              }}
                              style={{
                                padding: '0.625rem 0.75rem', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                backgroundColor: 'white', borderBottom: '1px solid #f3f4f6', fontSize: '0.8125rem'
                              }}
                              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3e8ff'}
                              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                            >
                              <span style={{ fontWeight: 500 }}>{event.name}</span>
                              <span style={{ color: '#9ca3af', fontSize: '0.75rem' }}>
                                {event.event_start ? new Date(event.event_start).toLocaleDateString() : 'No date'}
                              </span>
                            </div>
                          ))}
                        {events.filter(e => e.name?.toLowerCase().includes(eventSearchTerm.toLowerCase())).length === 0 && (
                          <div style={{ padding: '0.75rem', color: '#9ca3af', fontSize: '0.8125rem', textAlign: 'center' }}>No events found</div>
                        )}
                      </div>
                    )}
                    {selectedEvent && (
                      <div style={{ marginTop: '0.75rem', padding: '0.75rem', backgroundColor: '#f3e8ff', borderRadius: '0.5rem', border: '1px solid #e9d5ff', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                          <span style={{ fontWeight: 600, color: '#7c3aed' }}>Event: </span>
                          <span style={{ fontWeight: 500 }}>{selectedEvent.name}</span>
                          <span style={{ color: '#9ca3af', marginLeft: '0.5rem', fontSize: '0.8125rem' }}>
                            {selectedEvent.event_start ? new Date(selectedEvent.event_start).toLocaleDateString() : ''}
                          </span>
                        </div>
                        <button
                          onClick={() => { setSelectedEvent(null); setSendForm(prev => ({ ...prev, event_id: '' })); setEventSearchTerm(''); }}
                          style={{ background: 'none', border: 'none', color: '#9ca3af', cursor: 'pointer', fontSize: '1.25rem' }}
                        >×</button>
                      </div>
                    )}
                    {sendForm.event_id && !loadingPreview && (
                      <p className="field-hint">
                        {eventPurchaserCount} user{eventPurchaserCount !== 1 ? 's have' : ' has'} already purchased tickets.
                        {sendForm.send_push && !sendForm.send_email
                          ? ` Targeting ${totalUsers} app user${totalUsers !== 1 ? 's' : ''} (with push) who have not.`
                          : ` Targeting ${totalUsers} user${totalUsers !== 1 ? 's' : ''} who have not.`
                        }
                      </p>
                    )}
                    {!selectedEvent && !eventSearchTerm && (
                      <p className="field-hint">Search for an event by name to target users who haven't purchased tickets</p>
                    )}
                  </div>
                )}

                {sendForm.target_type === 'specific_user' && (
                  <div style={{ marginTop: '0.75rem' }}>
                    <div className="search-input-wrapper">
                      <Search size={16} className="search-icon" />
                      <input
                        type="text"
                        placeholder="Search by name or email..."
                        value={userSearchTerm}
                        onChange={(e) => setUserSearchTerm(e.target.value)}
                        className="search-input"
                      />
                      {userSearchTerm && (
                        <button className="clear-search" onClick={() => { setUserSearchTerm(''); setUserSearchResults([]); }}>×</button>
                      )}
                    </div>
                    {searchingUsers && <span className="loading-text" style={{ marginTop: '0.5rem', display: 'block' }}>Searching...</span>}
                    {userSearchResults.length > 0 && (
                      <div style={{ maxHeight: '250px', overflowY: 'auto', marginTop: '0.5rem', border: '1px solid #e5e7eb', borderRadius: '0.5rem' }}>
                        {userSearchResults.map(user => (
                          <div
                            key={user.uuid}
                            onClick={() => {
                              setSelectedUserId(user.uuid);
                              setUserSearchTerm('');
                              setUserSearchResults([]);
                            }}
                            style={{
                              padding: '0.625rem 0.75rem', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                              backgroundColor: selectedUserId === user.uuid ? '#f3e8ff' : 'white',
                              borderBottom: '1px solid #f3f4f6', fontSize: '0.8125rem'
                            }}
                          >
                            <div>
                              <span style={{ fontWeight: 500 }}>{user.nickname || user.first_name || 'No name'}</span>
                              <span style={{ color: '#9ca3af', marginLeft: '0.5rem', fontSize: '0.75rem' }}>{user.email}</span>
                            </div>
                            <div style={{ display: 'flex', gap: '0.25rem', alignItems: 'center' }}>
                              {user.hasApp && <Smartphone size={14} style={{ color: '#7c3aed' }} title="Has app" />}
                              {user.email && <Mail size={14} style={{ color: '#6b7280' }} title="Has email" />}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    {selectedUserId && (
                      <div style={{ marginTop: '0.75rem', padding: '0.75rem', backgroundColor: '#f3e8ff', borderRadius: '0.5rem', border: '1px solid #e9d5ff', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                          <span style={{ fontWeight: 600, color: '#7c3aed' }}>Selected: </span>
                          <span style={{ fontWeight: 500 }}>{previewUsers[0]?.nickname || previewUsers[0]?.first_name || previewUsers[0]?.email || 'Loading...'}</span>
                          {previewUsers[0]?.email && <span style={{ color: '#9ca3af', marginLeft: '0.5rem', fontSize: '0.8125rem' }}>{previewUsers[0]?.email}</span>}
                        </div>
                        <button
                          onClick={() => { setSelectedUserId(null); setUserSearchTerm(''); }}
                          style={{ background: 'none', border: 'none', color: '#9ca3af', cursor: 'pointer', fontSize: '1.25rem' }}
                        >×</button>
                      </div>
                    )}
                    {!selectedUserId && !userSearchTerm && (
                      <p className="field-hint">Search for a user by name or email to select them as the recipient</p>
                    )}
                  </div>
                )}

                {/* Recipient count display */}
                <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid #e5e7eb' }}>
                  {loadingPreview ? (
                    <span className="loading-text">Loading recipients...</span>
                  ) : (
                    <span className="count-badge">
                      <Smartphone size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: '0.25rem' }} />
                      {totalUsers} app user{totalUsers !== 1 ? 's' : ''} will receive push
                    </span>
                  )}
                </div>
              </div>

              {/* Step 1 navigation */}
              <div className="send-step-nav">
                <button
                  className="btn-cancel"
                  onClick={() => navigate('/admin/notifications')}
                  disabled={sending}
                >
                  Cancel
                </button>
                <button
                  className="btn-send"
                  onClick={() => setCurrentStep(2)}
                  disabled={!sendForm.send_push && !sendForm.send_email}
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Step 2: Compose */}
        {currentStep === 2 && (
          <div className="send-notification-content">
            {/* Left Column - Compose fields */}
            <div className="form-column">
              <div className="form-section">
                <h3>Message Content</h3>

                <div className="form-group">
                  <label>Title *</label>
                  <input
                    type="text"
                    value={sendForm.title}
                    onChange={(e) => setSendForm(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="Notification title"
                    disabled={sending}
                  />
                </div>

                <div className="form-group">
                  <label>Message *</label>
                  <textarea
                    value={sendForm.message}
                    onChange={(e) => setSendForm(prev => ({ ...prev, message: e.target.value }))}
                    placeholder="Notification message"
                    rows={4}
                    disabled={sending}
                  />
                </div>

                <div className="form-group">
                  <label>Category *</label>
                  <select
                    value={sendForm.category_key}
                    onChange={(e) => setSendForm(prev => ({ ...prev, category_key: e.target.value }))}
                    disabled={sending}
                  >
                    <option value="">Select category</option>
                    {categories.map((category) => (
                      <option key={category.category_key} value={category.category_key}>
                        {category.title}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Priority</label>
                  <select
                    value={sendForm.priority}
                    onChange={(e) => setSendForm(prev => ({ ...prev, priority: parseInt(e.target.value) }))}
                    disabled={sending}
                  >
                    <option value={1}>Normal</option>
                    <option value={2}>High</option>
                    <option value={3}>Urgent</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>
                    <Link size={14} style={{ marginRight: 6 }} />
                    Action URL (Deep Link)
                  </label>
                  <input
                    type="text"
                    value={sendForm.action_url}
                    onChange={(e) => setSendForm(prev => ({ ...prev, action_url: e.target.value }))}
                    placeholder="/collections/123 or /events/456"
                    disabled={sending}
                  />
                  <p className="field-hint">
                    Where users navigate when tapping the notification
                  </p>
                </div>
              </div>

              {/* Email-specific options */}
              {sendForm.send_email && (
                <div className="form-section email-section">
                  <h3><Mail size={18} /> Email Options</h3>

                  <div className="form-group">
                    <label>Email Subject (optional)</label>
                    <input
                      type="text"
                      value={sendForm.email_subject}
                      onChange={(e) => setSendForm(prev => ({ ...prev, email_subject: e.target.value }))}
                      placeholder="Uses notification title if empty"
                      disabled={sending}
                    />
                  </div>

                  <div className="form-group">
                    <label>Email Template</label>
                    <select
                      value={sendForm.email_template}
                      onChange={(e) => setSendForm(prev => ({ ...prev, email_template: e.target.value }))}
                      disabled={sending}
                    >
                      <option value="">Default Template</option>
                      {emailTemplates.map((template) => (
                        <option key={template.id} value={template.mandrill_template_name || template.template_key}>
                          {template.template_key} - {template.subject}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              )}

              {/* Step 2 navigation */}
              <div className="send-step-nav">
                <button
                  className="btn-cancel"
                  onClick={() => setCurrentStep(1)}
                  disabled={sending}
                >
                  Back
                </button>
                <button
                  className="btn-send"
                  onClick={() => setCurrentStep(3)}
                  disabled={!sendForm.title || !sendForm.message}
                >
                  Next
                </button>
              </div>
            </div>

            {/* Right Column - Recipient preview */}
            <div className="preview-column">
              <RecipientPreview />
            </div>
          </div>
        )}

        {/* Step 3: Review & Send */}
        {currentStep === 3 && (
          <div className="send-notification-content" style={{ gridTemplateColumns: '1fr' }}>
            <div className="form-column">
              {/* Summary card */}
              <div className="send-review-card">
                <div className="send-review-label">Channels</div>
                <div className="send-review-value">
                  {[sendForm.send_push && '📱 Push', sendForm.send_email && '✉️ Email'].filter(Boolean).join(' + ')}
                </div>
              </div>

              <div className="send-review-card">
                <div className="send-review-label">Audience</div>
                <div className="send-review-value">
                  {sendForm.target_type === 'all_users' && 'All Mobile Users'}
                  {sendForm.target_type === 'user_types' && `User Types: ${sendForm.user_types.length > 0 ? sendForm.user_types.join(', ') : 'All'}`}
                  {sendForm.target_type === 'collection' && `Collection: ${selectedCollection?.title || sendForm.collection_id}`}
                  {sendForm.target_type === 'specific_user' && `Specific User: ${previewUsers[0]?.nickname || previewUsers[0]?.email || (selectedUserId ? 'Selected' : 'None selected')}`}
                </div>
                <div className="send-review-label" style={{ marginTop: '0.5rem' }}>Recipients</div>
                <div className="send-review-value">{totalUsers} user{totalUsers !== 1 ? 's' : ''}</div>
              </div>

              <div className="send-review-card">
                <div className="send-review-label">Message</div>
                <div className="send-review-value" style={{ fontWeight: 600 }}>{sendForm.title || <em style={{ color: '#9ca3af' }}>No title</em>}</div>
                <div className="send-review-value" style={{ marginTop: '0.375rem', color: '#6b7280' }}>{sendForm.message || <em style={{ color: '#9ca3af' }}>No message</em>}</div>
                {sendForm.category_key && (
                  <>
                    <div className="send-review-label" style={{ marginTop: '0.5rem' }}>Category</div>
                    <div className="send-review-value">{sendForm.category_key}</div>
                  </>
                )}
                <div className="send-review-label" style={{ marginTop: '0.5rem' }}>Priority</div>
                <div className="send-review-value">
                  {sendForm.priority === 1 ? 'Normal' : sendForm.priority === 2 ? 'High' : 'Urgent'}
                </div>
                {sendForm.action_url && (
                  <>
                    <div className="send-review-label" style={{ marginTop: '0.5rem' }}>Action URL</div>
                    <div className="send-review-value" style={{ fontFamily: 'monospace', fontSize: '0.8125rem' }}>{sendForm.action_url}</div>
                  </>
                )}
              </div>

              {sendForm.send_email && (
                <div className="send-review-card">
                  <div className="send-review-label">Email Subject</div>
                  <div className="send-review-value">{sendForm.email_subject || sendForm.title || <em style={{ color: '#9ca3af' }}>Uses notification title</em>}</div>
                  <div className="send-review-label" style={{ marginTop: '0.5rem' }}>Template</div>
                  <div className="send-review-value">{sendForm.email_template || 'Default Template'}</div>
                </div>
              )}

              {/* Step 3 navigation */}
              <div className="send-step-nav">
                <button
                  className="btn-cancel"
                  onClick={() => setCurrentStep(2)}
                  disabled={sending}
                >
                  Back
                </button>
                <button
                  className="btn-send"
                  onClick={handleSendNotification}
                  disabled={
                    sending ||
                    !sendForm.title ||
                    !sendForm.message ||
                    !sendForm.category_key ||
                    previewUsers.length === 0 ||
                    (!sendForm.send_push && !sendForm.send_email) ||
                    (sendForm.target_type === 'collection' && !sendForm.collection_id)
                  }
                >
                  <Send size={18} />
                  {`Send ${[sendForm.send_push && '📱 Push', sendForm.send_email && '✉️ Email'].filter(Boolean).join(' + ')} to ${totalUsers} User${totalUsers !== 1 ? 's' : ''}`}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Progress / Completion modal overlay */}
        {(sending || sendComplete) && (
          <div className="send-progress-overlay">
            <div className="send-progress-modal">
              <h3 style={{ color: '#111827', margin: '0 0 1rem 0' }}>
                {sendComplete ? 'Send Complete' : 'Sending Notifications...'}
              </h3>

              <div className="progress-stats">
                <div className="stat">
                  <span className="stat-label">Total Users</span>
                  <span className="stat-value">{sendProgress.total}</span>
                </div>
                {sendForm.send_push && (
                  <>
                    <div className="stat success">
                      <Smartphone size={16} />
                      <span className="stat-value">{sendProgress.pushSent}</span>
                    </div>
                    {sendProgress.pushFailed > 0 && (
                      <div className="stat error">
                        <XCircle size={16} />
                        <span className="stat-value">{sendProgress.pushFailed}</span>
                      </div>
                    )}
                  </>
                )}
                {sendForm.send_email && (
                  <>
                    <div className="stat success">
                      <Mail size={16} />
                      <span className="stat-value">{sendProgress.emailSent}</span>
                    </div>
                    {sendProgress.emailFailed > 0 && (
                      <div className="stat error">
                        <XCircle size={16} />
                        <span className="stat-value">{sendProgress.emailFailed}</span>
                      </div>
                    )}
                  </>
                )}
              </div>

              <div className="progress-bar-container">
                <div
                  className="progress-bar"
                  style={{ width: `${progressPercentage}%` }}
                />
              </div>
              <div className="progress-percentage">{progressPercentage}%</div>

              {sendProgress.current && (
                <div className="current-user">
                  <Loader className="spinner" size={16} />
                  Sending to: {sendProgress.current}
                </div>
              )}

              <div className="progress-results">
                {sendProgress.results.slice(-10).reverse().map((result, idx) => (
                  <div key={idx} className={`result-item ${result.success ? 'success' : 'error'}`}>
                    {result.success ? (
                      <CheckCircle size={16} />
                    ) : (
                      <XCircle size={16} />
                    )}
                    <span>{result.user || result.userId}</span>
                    {result.channel && <span className="result-channel">({result.channel})</span>}
                  </div>
                ))}
              </div>

              {sendComplete && (
                <button
                  onClick={() => { setSendComplete(false); setCurrentStep(1); }}
                  className="btn-send"
                  style={{ marginTop: '1rem', width: '100%', justifyContent: 'center' }}
                >
                  Done
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SendNotificationPage;
